<template>
  <div v-if="!result" class="calculation-steps">
    <h2 class="h4">
      Calculating Kurtosis (Formula)
    </h2>
    <p class="bottom-margins">
      The Kurtosis Calculator is a helpful shortcut that speeds up your work. But you can also calculate a population's or a sample’s kurtosis by hand. The formulas, however, are slightly different.
    </p>
    <p>
      The <span class="bolded-texts">sample excess kurtosis</span> is calculated as follows:
    </p>
    <div class="formulas">
      \[K=\left\{\frac{n(n+1)}{(n-1)(n-2)(n-3)}\sum_{i=1}^{n}\left(\frac{X_i-\bar{X}}{s}\right)^4\right\}-\frac{3{(n-1)}^2}{(n-2)(n-3)}\]
    </div>
    <p class="bottom-margins">
      In this formula, <span class="bolded-texts">n</span> denotes the number of observations in the sample, <span class="inline-formulas">\(\bar{X} \)</span> is the sample mean, whereas <span class="bolded-texts">s</span> is the sample standard deviation.
    </p>
    <p>Meanwhile, <span class="bolded-texts">population excess kurtosis</span> is calculated using the following formula:</p>
    <div class="formulas">
      \[\gamma_2=\frac{1}{N}\sum_{i=1}^{n}\left(\frac{X_i-\bar{\mu}}{\sigma}\right)^4-3\]
    </div>
    <p>In this formula, <span class="bolded-texts">n</span> denotes the number of observations in the population, <span class="inline-formulas">\(\mu\)</span> is the population mean, whereas <span class="inline-formulas">\(\sigma\)</span> is the population standard deviation. </p>
    <p class="top-margins">
      A positive kurtosis value means that a distribution is more peaked than a normal distribution, while a negative one means it is less peaked. It is important to note here that a normal distribution has a kurtosis value of 0.
    </p>
    <div class="formulas">
      \[K=\left\{\frac{n(n+1)}{(n-1)(n-2)(n-3)}\sum_{i=1}^{n}\left(\frac{X_i-\bar{X}}{s}\right)^4\right\}-\frac{3{(n-1)}^2}{(n-2)(n-3)}\]
    </div>
    <div class="formulas">
      \[\gamma_2=\frac{1}{N}\sum_{i=1}^{n}\left(\frac{X_i-\bar{\mu}}{\sigma}\right)^4-3\]
    </div>
    <p>Please note that you need at least four observations in your dataset. Otherwise, the denominator would be equal to 0.</p>
    <h2 class="h4 additional-h2">
      How to Calculate Sample and Population Kurtosis: A Step-by-Step Guide
    </h2>
    <p class="bottom-margins">
      To calculate the kurtosis of a dataset, you can follow these steps:
    </p>
    <ol>
      <li>
        <p>
          Calculate the <span class="bolded-texts">sample</span> or <span class="bolded-texts">population mean</span> of the dataset:
        </p>
        <p class="formulas">
          \[\bar{X}=\frac{\sum_{i=1}^{n}X_i}{n}\]
        </p>
        <p class="formulas math-jax-bottom-margins">
          \[\mu=\frac{\sum_{i=1}^{n}X_i}{n}\]
        </p>
      </li>
      <li>
        <p>
          Find the <span class="bolded-texts">sample</span> or <span class="bolded-texts">population standard deviation</span> of the dataset:
        </p>
        <p class="formulas">
          \[s=\sqrt{\sum_{i=1}^{n}\frac{(X_i-\bar{X})^2}{n-1}}\]
        </p>
        <p class="formulas math-jax-bottom-margins">
          \[\sigma=\sqrt{\sum_{i=1}^{n}\frac{(X_i-\mu)^2}{n}}\]
        </p>
      </li>
      <li>
        <p>
          Standardize each data point by subtracting the <span class="bolded-texts">mean</span> and dividing by the <span class="bolded-texts">sample</span> or <span class="bolded-texts">population standard deviation</span>:
        </p>
        <p class="formulas">
          \[Z=\frac{X_i-\bar{X}}{s}\]
        </p>
        <p class="formulas math-jax-bottom-margins">
          \[Z=\frac{X_i-\mu}{\sigma}\]
        </p>
      </li>
      <li>
        <p>
          Raise each standardized value to the power of 4:
        </p>
        <p class="formulas">
          \[\left(\frac{X_i-\bar{X}}{s}\right)^4\]
        </p>
        <p class="formulas math-jax-bottom-margins">
          \[\left(\frac{X_i-\mu}{\sigma}\right)^4\]
        </p>
      </li>
      <li>
        <p>
          Sum all the standardized values raised to the power of 4:
        </p>
        <p class="formulas">
          \[\sum_{i=1}^{n}\left(\frac{X_i-\bar{X}}{s}\right)^4\]
        </p>
        <p class="formulas math-jax-bottom-margins">
          \[\sum_{i=1}^{n}\left(\frac{X_i-\mu}{\sigma}\right)^4\]
        </p>
      </li>
      <li>
        <p>
          Multiply the result by n(n+1)/(n-1)(n-2)(n-3) and subtract 3(n-1)^3/(n-2)(n-3) to obtain the <span class="bolded-texts">sample kurtosis</span>, or multiply it by 1/N, where n is the number of data points in the population, and subtract 3 to obtain the <span class="bolded-texts">population kurtosis</span>:
        </p>
        <div class="formulas">
          \[K=\left\{\frac{n(n+1)}{(n-1)(n-2)(n-3)}\sum_{i=1}^{n}\left(\frac{X_i-\bar{X}}{s}\right)^4\right\}-\frac{3{(n-1)}^2}{(n-2)(n-3)}\]
        </div>
        <div class="formulas">
          \[\gamma_2=\frac{1}{N}\sum_{i=1}^{n}\left(\frac{X_i-\bar{\mu}}{\sigma}\right)^4-3\]
        </div>
      </li>
    </ol>

    <p>
      Note that the subtraction of 3 from the population formula is to make the kurtosis of a normal distribution equal to 0.
    </p>
  </div>

  <div v-else :key="result" class="calculation-steps result">
    <h2 class="h4">
      Calculation Steps:
    </h2>
    <p class="bottom-margins">
      Follow these steps to obtain the sample kurtosis of your dataset:
    </p>
    <ol>
      <li>
        <p>
          Calculate the <span class="bolded-texts">mean</span> of the dataset:
        </p>
        <p v-if="calculationData.method === 'sample'" class="formulas math-jax-top-bottom-margins">
          \[\bar{X}=\frac{\sum_{i=1}^{n}X_i}{n}=\frac{ {{ additionString }} }{ {{ calculationData.size }} }={{ meanStandardDeviationFormatting[0] }}\]
        </p>
        <p v-else class="formulas math-jax-top-bottom-margins">
          \[\mu=\frac{\sum_{i=1}^{n}X_i}{n}=\frac{ {{ additionString }} }{ {{ calculationData.size }} }={{ meanStandardDeviationFormatting[0] }}\]
        </p>
      </li>
      <li>
        <p>
          Find the <span class="bolded-texts">{{ currentMethodName }} standard</span> deviation of the dataset:
        </p>
        <p v-if="calculationData.method === 'sample'" class="formulas math-jax-top-bottom-margins">
          \[s=\sqrt{\sum_{i=1}^{n}{\frac{(X_i-\bar{X})^2}{n-1}=}}\sum_{i=1}^{n}{\frac{ {{ sqrString }} }{ {{ calculationData.size }}-1}={{ meanStandardDeviationFormatting[1] }}}\]
        </p>
        <p v-else class="formulas math-jax-top-bottom-margins">
          \[\sigma=\sqrt{\sum_{i=1}^{n}\frac{(X_i-\mu)^2}{n}}=\sum_{i=1}^{n}{\frac{ {{ sqrString }} }{ {{ calculationData.size }} }={{ meanStandardDeviationFormatting[1] }}}\]
        </p>
      </li>
      <li>
        <p>
          Standardize each data point by subtracting the mean and dividing by the sample standard deviation. For example, for the first variable we have the following:
        </p>
        <p v-if="calculationData.method === 'sample'" class="formulas math-jax-top-bottom-margins">
          \[Z=\frac{X_i-\bar{X}}{s}=\frac{ {{ calculationData.rawDataMeanArray[0] }}-{{ meanStandardDeviationFormatting[0] }} }{ {{ meanStandardDeviationFormatting[1] }} }={{ standardizeExampleResult }}\]
        </p>
        <p v-else class="formulas math-jax-top-bottom-margins">
          \[Z=\frac{X_i-\mu}{\sigma}=\frac{ {{ calculationData.rawDataMeanArray[0] }}-{{ meanStandardDeviationFormatting[0] }} }{ {{ meanStandardDeviationFormatting[1] }} }={{ standardizeExampleResult }}\]
        </p>
        <p class="bottom-margins">
          We obtain the rest of the standardized variables in the same way.
        </p>
      </li>
      <li>
        <p>
          Raise each standardized value to the power of 4:
        </p>
        <p v-if="calculationData.method === 'sample'" class="formulas math-jax-top-bottom-margins">
          \[\left(\frac{X_i-\bar{X}}{s}\right)^4=\left(\frac{ {{ calculationData.rawDataMeanArray[0] }}-{{ meanStandardDeviationFormatting[0] }} }{ {{ meanStandardDeviationFormatting[1] }} }\right)^4={{ powerOfFour }}\]
        </p>
        <p v-else class="formulas math-jax-top-bottom-margins">
          \[\left(\frac{X_i-\mu}{\sigma}\right)^4=\left(\frac{ {{ calculationData.rawDataMeanArray[0] }}-{{ meanStandardDeviationFormatting[0] }} }{ {{ meanStandardDeviationFormatting[1] }} }\right)^4={{ powerOfFour }}\]
        </p>
      </li>
      <li>
        <p>
          Sum all the standardized values raised to the power of 4:
        </p>
        <p v-if="calculationData.method === 'sample'" class="formulas math-jax-top-bottom-margins">
          \[\sum_{i=1}^{n}{\left(\frac{X_i-\bar{X}}{s}\right)^4=\sum_{i=1}^{n}{ {{ raisedToFourString }} }={{ calculationData.raisedToFourStandardizationsValuesSum }}}\]
        </p>
        <p v-else class="formulas math-jax-top-bottom-margins">
          \[\sum_{i=1}^{n}{\left(\frac{X_i-\mu}{\sigma}\right)^4=\sum_{i=1}^{n}{ {{ raisedToFourString }} }={{ calculationData.raisedToFourStandardizationsValuesSum }}}\]
        </p>
      </li>
      <li>
        <div v-if="calculationData.method === 'sample'">
          <p>
            Multiplying the result by <span>n (n+1) / (n-1) (n-2) (n-3)</span> and subtract <span>(n-1)2 / (n-2) (n-3)</span> from the product to obtain the <span class="bolded-texts">sample kurtosis</span>:
          </p>
          <p class="formulas top-margins">
            \[K=\left\{\frac{n(n+1)}{(n-1)(n-2)(n-3)}\sum_{i=1}^{n}\left(\frac{X_i-\bar{X}}{s}\right)^4\right\}-\frac{3{(n-1)}^2}{(n-2)(n-3)}=\left\{\frac{ {{ calculationData.size }}({{ calculationData.size }}+1)}{({{ calculationData.size }}-1)({{ calculationData.size }}-2)({{ calculationData.size }}-3)}{{ calculationData.raisedToFourStandardizationsValuesSum }}\right\}-\frac{3{({{ calculationData.size }}-1)}^2}{({{ calculationData.size }}-2)({{ calculationData.size }}-3)}={{ calculationData.kurtosis }}\]
          </p>
        </div>
        <div v-else>
          <p>
            Multiply the result by 1/ n and subtract 3 from the product to obtain the <span class="bolded-texts">population kurtosis</span>:
          </p>
          <p class="formulas top-margins">
            \[\gamma_2=\frac{1}{N}\sum_{i=1}^{n}\left(\frac{X_i-\bar{\mu}}{\sigma}\right)^4-3\]
          </p>
          <p class="formulas">
            \[\gamma_2=\frac{1}{N}\sum_{i=1}^{n}\left(\frac{X_i-\bar{\mu}}{\sigma}\right)^4-3=\left(\frac{1}{ {{ calculationData.size }} }{{ calculationData.raisedToFourStandardizationsValuesSum }}\right)-3={{ calculationData.kurtosis }}\]
          </p>
        </div>
      </li>
    </ol>

    <div v-if="calculationData.method === 'sample'">
      <div class="python-r-codes python">
        <span class="img-text"><img alt="Python icon" src="../../../../assets/images/icon-python-calc.png"> <h4 class="h6">Your Result in Python:</h4></span>
        <p class="codes-info">
          The following Python code should provide the mean, median, and mode using the input data you entered.
        </p>
        <div class="codes-container">
          <div class="codes">
            <pre
              class="language-python"
            ><code id="python-sample"># Import relevant library

from scipy.stats import kurtosis

data = <span class="no-wrap">{{ inputToNumbers }}</span>

# Calculate the sample kurtosis using scipy.stats

kurtosis = kurtosis(data, bias=False)

print("Sample kurtosis:", kurtosis) </code></pre>
          </div>
        </div>
        <button class="btn-link-accent" :class="{ copied: copyCodeOrCopiedPython === 'Copied' }" @click="copyCode('python-sample')">
          {{ copyCodeOrCopiedPython }}
        </button>
      </div>
      <div class="python-r-codes">
        <span class="img-text"><img alt="Icon for R language" src="../../../../assets/images/icon-r-calc.png"> <h4 class="h6">Your Result in R:</h4></span>
        <p class="codes-info">
          The following R code should provide the mean, median, and mode using the input data you entered.
        </p>
        <div class="codes-container">
          <div class="codes">
            <pre
              class="language-r"
            ><code id="r-sample"># Install and load the e1071 package (if not installed)

install.packages("e1071")

library(e1071)

# Create vector of data

data &lt;- c(<span class="no-wrap">{{ arrayToString }}</span>)

# Calculate sample kurtosis using e1071 package

kurtosis(data, type = 2) </code></pre>
          </div>
        </div>
        <button class="btn-link-accent" :class="{ copied: copyCodeOrCopiedR === 'Copied' }" @click="copyCode('r-sample')">
          {{ copyCodeOrCopiedR }}
        </button>
      </div>
    </div>
    <div v-else>
      <div class="python-r-codes python">
        <span class="img-text"><img alt="Python icon" src="../../../../assets/images/icon-python-calc.png"> <h4 class="h6">Your Result in Python:</h4></span>
        <p class="codes-info">
          The following Python code should provide the mean, median, and mode using the input data you entered.
        </p>
        <div class="codes-container">
          <div class="codes">
            <pre
              class="language-python"
            ><code id="python-population"># Import relevant library

from scipy.stats import kurtosis

data = <span class="no-wrap">{{ inputToNumbers }}</span>

# Calculate the sample kurtosis using scipy.stats

kurtosis = kurtosis(data)

print("Population kurtosis:", kurtosis) </code></pre>
          </div>
        </div>
        <button class="btn-link-accent" :class="{ copied: copyCodeOrCopiedPython === 'Copied' }" @click="copyCode('python-population')">
          {{ copyCodeOrCopiedPython }}
        </button>
      </div>
      <div class="python-r-codes">
        <span class="img-text"><img alt="Icon for R language" src="../../../../assets/images/icon-r-calc.png"> <h4 class="h6">Your Result in R:</h4></span>
        <p class="codes-info">
          The following R code should provide the mean, median, and mode using the input data you entered.
        </p>
        <div class="codes-container">
          <div class="codes">
            <pre
              class="language-r"
            ><code id="r-population"># Install and load the e1071 package (if not installed)

install.packages("e1071")

library(e1071)

# Create vector of data

data &lt;- c(<span class="no-wrap">{{ arrayToString }}</span>)

# Calculate population kurtosis using e1071 package

kurtosis (data, type = 1) </code></pre>
          </div>
        </div>
        <button class="btn-link-accent" :class="{ copied: copyCodeOrCopiedR === 'Copied' }" @click="copyCode('r-population')">
          {{ copyCodeOrCopiedR }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import calculatorsStepsMixin from '@itbpbg/lms-components/src/mixins/calculators/steps';

export default {
  mixins: [calculatorsStepsMixin],
  computed: {
    additionString() {
      const { length } = this.calculationData.rawDataMeanArray;
      const rawDataMeanArray = this.calculationData.rawDataMeanArray.map((num, i) => (length - 1 > i ? `${num} + ` : num));
      return rawDataMeanArray.join('');
    },
    sqrString() {
      const { length } = this.calculationData.rawDataMeanArray;
      const rawDataMeanArray = this.calculationData.rawDataMeanArray.map((num, i) => (length - 1 > i ? `(${num} - ${this.meanStandardDeviationFormatting[0]})^2 + ` : `(${num} - ${this.meanStandardDeviationFormatting[0]})^2`));
      return rawDataMeanArray.join('');
    },
    raisedToFourString() {
      const { length } = this.calculationData.rawDataMeanArray;
      const rawDataMeanArray = this.calculationData.rawDataMeanArray.map((num, i) => (length - 1 > i ? `\\left(\\frac{${num}-${this.meanStandardDeviationFormatting[0]}}{${this.meanStandardDeviationFormatting[1]}}\\right)^4 + ` : `\\left(\\frac{${num}-${this.meanStandardDeviationFormatting[0]}}{${this.meanStandardDeviationFormatting[1]}}\\right)^4`));
      return rawDataMeanArray.join('');
    },
    standardizeExampleResult() {
      return ((this.calculationData.rawDataMeanArray[0] - this.calculationData.mean) / this.calculationData.standardDeviation).toFixed(this.calculationData.selectedDecimalPlaces);
    },
    powerOfFour() {
      return (((this.calculationData.rawDataMeanArray[0] - this.calculationData.mean) / this.calculationData.standardDeviation) ** 4).toFixed(this.calculationData.selectedDecimalPlaces);
    },
    meanStandardDeviationFormatting() {
      const meanStandardDeviationArray = [this.calculationData.mean.toString(), this.calculationData.standardDeviation.toString()];

      for (let i = 0; i < meanStandardDeviationArray.length; i += 1) {
        if (meanStandardDeviationArray[i].split('').includes('e')) {
          [meanStandardDeviationArray[i]] = meanStandardDeviationArray[i].split('e');
        }

        meanStandardDeviationArray[i] = Number(meanStandardDeviationArray[i]).toFixed(this.calculationData.selectedDecimalPlaces);
      }

      return meanStandardDeviationArray;
    },
    arrayToString() {
      return this.calculationData.rawDataMeanArray.join(', ');
    },
    currentMethodName() {
      return this.calculationData.method === 'sample' ? 'sample' : 'population';
    },
  },
};
</script>

<style lang="scss" scoped>
.calculation-steps {
  p {
    margin: 0;
  }

  .inline-formulas {
    ::v-deep .MathJax_SVG_Display {
      display: unset !important;
    }
  }

  .python-r-codes.python {
    margin-top: var(--spacing-80);
  }

  ul {
    padding-left: 0;
  }

  .additional-h2 {
    margin-top: var(--spacing-40);
  }

  .second-additional-h2 {
    margin-top: var(--spacing-24);
  }

  .math-jax-bottom-margins {
    margin-bottom: var(--spacing-16);
  }

  .math-jax-top-bottom-margins {
    margin: var(--spacing-16) 0;
  }
}

@include bp($bp-mobile) {
  div .calculation-steps {
    padding-top: var(--spacing-40);

    .h4 {
      text-align: left;
    }
  }
}

@import '../../../../assets/sass/calculators/calculation-steps.scss';
</style>
